"use client"

import { Play, Volume2, VolumeX } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { cn } from "@repo/ui/lib/utils"

/** Fichier vidéo démo présent dans `apps/docs/public/`. */
export const HOME_DEMO_VIDEO_SRC = "/video-optimized.mp4"

interface ServiceVideoProps {
  src?: string
  poster: string
  alt: string
  className?: string
}

export function ServiceVideo({
  src = HOME_DEMO_VIDEO_SRC,
  poster,
  alt,
  className,
}: ServiceVideoProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [hasError, setHasError] = useState(false)
  const [isMuted, setIsMuted] = useState(true)
  const [isInView, setIsInView] = useState(false)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    const node = containerRef.current
    if (!node) return

    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry?.isIntersecting ?? false),
      { rootMargin: "80px", threshold: 0.35 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const syncPlayback = useCallback(async () => {
    const video = videoRef.current
    if (!video || hasError) return

    if (isInView) {
      try {
        await video.play()
      } catch {
        // Autoplay peut être bloqué — le poster reste visible
      }
    } else {
      video.pause()
    }
  }, [hasError, isInView])

  useEffect(() => {
    void syncPlayback()
  }, [syncPlayback])

  const toggleMute = (): void => {
    const video = videoRef.current
    if (!video) return
    const nextMuted = !video.muted
    video.muted = nextMuted
    setIsMuted(nextMuted)
  }

  if (hasError) {
    return (
      <div
        className={cn(
          "relative flex size-full items-center justify-center overflow-hidden rounded-xl bg-muted",
          className,
        )}
      >
        <img src={poster} alt={alt} className="size-full object-cover" />
        <div className="absolute inset-0 flex items-center justify-center bg-foreground/15">
          <div className="flex size-14 items-center justify-center rounded-full bg-primary/90 text-primary-foreground shadow-forest-lg">
            <Play className="size-7" aria-hidden />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        "group relative flex size-full items-center justify-center overflow-hidden rounded-xl bg-muted",
        className,
      )}
    >
      {!isReady ? (
        <img
          src={poster}
          alt=""
          aria-hidden
          className="absolute inset-0 size-full object-cover opacity-90"
        />
      ) : null}

      <video
        ref={videoRef}
        src={src}
        poster={poster}
        muted={isMuted}
        loop
        playsInline
        preload={isInView ? "auto" : "none"}
        onLoadedData={() => setIsReady(true)}
        onCanPlay={() => setIsReady(true)}
        onError={() => setHasError(true)}
        className={cn(
          "max-h-full w-full object-contain transition-opacity duration-300",
          isReady ? "opacity-100" : "opacity-0",
        )}
        aria-label={alt}
      />

      <button
        type="button"
        onClick={toggleMute}
        className="absolute bottom-3 right-3 flex size-9 items-center justify-center rounded-full border border-white/25 bg-black/45 text-white opacity-0 backdrop-blur-sm transition-opacity duration-200 hover:bg-black/60 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary group-hover:opacity-100"
        aria-label={isMuted ? "Activer le son" : "Couper le son"}
      >
        {isMuted ? <VolumeX className="size-4" aria-hidden /> : <Volume2 className="size-4" aria-hidden />}
      </button>
    </div>
  )
}
